import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Pencil, Trash2 } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { DetailGrid } from '@/components/backoffice/DetailGrid'
import { Button } from '@/components/ui/button'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { DocumentUploader } from '@/components/shared/DocumentUploader'
import type { NomineeDirectorTypeSummary, DocumentAttachment } from '@/types'
import { formatCents } from '@/lib/utils'

type Props = {
  type: NomineeDirectorTypeSummary & { document_templates: DocumentAttachment[] }
}

export default function NomineeDirectorTypesShow({ type }: Props) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  return (
    <BackofficeLayout title={type.name} breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Nominee Director Types', href: '/backoffice/nominee_director_types' }, { label: type.name }]}>
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">{type.name}</h1>
          <p className="text-sm text-muted-foreground">{type.code}</p>
        </div>

        <div className="flex gap-2">
          <Button size="sm" variant="outline" asChild className="h-8 text-[13px]">
            <Link href={`/backoffice/nominee_director_types/${type.id}/edit`}><Pencil className="mr-1.5 h-3 w-3" />Edit</Link>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowDeleteDialog(true)} className="h-8 text-[13px] text-destructive hover:text-destructive">
            <Trash2 className="mr-1.5 h-3 w-3" />Delete
          </Button>
        </div>

        <DetailGrid items={[
          { label: 'Jurisdiction', value: type.jurisdiction_label || '—' },
          { label: 'Gov Fee', value: formatCents(type.gov_fee_cents) },
          { label: 'Service Fee', value: formatCents(type.service_fee_cents) },
          { label: 'Min Duration', value: `${type.min_duration_months} months` },
          { label: 'Processing', value: type.processing_days_min && type.processing_days_max ? `${type.processing_days_min}–${type.processing_days_max} days` : '—' },
        ]} />

        {type.description && (
          <div className="py-3 border-b border-border/40">
            <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Description</p>
            <p className="text-sm">{type.description}</p>
          </div>
        )}

        {/* Document Templates */}
        <DocumentUploader
          title="Document Templates"
          documents={type.document_templates || []}
          uploadPath={`/backoffice/nominee_director_types/${type.id}/document_templates`}
          deletePath={(docId) => `/backoffice/nominee_director_types/${type.id}/document_templates/${docId}`}
        />

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {type.name}?</AlertDialogTitle>
              <AlertDialogDescription>This will permanently delete this nominee director type. Types with existing services cannot be deleted.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => router.delete(`/backoffice/nominee_director_types/${type.id}`)}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </BackofficeLayout>
  )
}
